import {
  EllipsisOutlined,
  LinkOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import styled from 'styled-components';

import HandlerIcon from '@/assets/icons/HandlerIcon';
import MissingChartIcon from '@/assets/icons/MissingChartIcon';
import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { CenterFlex, Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Empty from '@/components/Empty/Empty';
import { HoverItem, HoverWrapper } from '@/components/Hover';
import Menu from '@/components/Menu/Menu';
import Modal from '@/components/Modal/Modal';
import { Stage } from '@/components/Stage/Stage';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { HeySynclyMessageDisplay } from '@/entities/hey-syncly';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import {
  AnalysisState,
  type ChartAnalysisFeedbackListModalOptions,
} from '@/features/Analysis/types';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services';
import useDashboard2Store from '@/stores/Dashboard2Store';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { radius } from '@/styles/sizes';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

import useDashboardCardDrag from '../../hooks/useDashboardCardDrag';
import {
  DASHBOARD_CARD_ANALYSIS_ALIAS_DATASET_KEY,
  DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY,
} from '../../types';
import type { DashboardCardProps } from '../DashboardCard';
import AnalysisBoxRenameModal from '../Modals/AnalysisBoxRenameModal';
import ChartAnalysisSettingModal from '../Modals/ChartAnalysisSettingModal';
import DashboardCardStyles from '../styles/DashboardCardStyles';

const { StyledDragArea, StyledDashboardCard, StyledDashboardCardWrapper } =
  DashboardCardStyles;

const DashboardAnalysisBox = (props: DashboardCardProps) => {
  const setPopup = usePopup();
  const queryClient = useQueryClient();
  const addToast = useToast();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const dashboard = useDashboard2Store((state) => state.currDashboard);
  const analysisMap = useDashboard2Store((state) => state.analysisMap);
  const setAnalysisItem = useDashboard2Store((state) => state.setAnalysisItem);
  const currDashboard = useDashboard2Store((state) => state.currDashboard);
  const renameModalState = useModalState();
  const chartAnalysisSettingModalState = useModalState();

  const {
    index,
    rowIndex,
    setSomeDragging,
    column,
    rowId,
    width,
    dashboardId,
  } = props;

  const analysisId = column.analysis?.alias;

  if (!analysisId) {
    throw new Error(`analysis is not exist`);
  }
  if (!currDashboard) throw new Error('currDashboard is not exist');

  const chatMessageContainerRef = useRef<HTMLDivElement>(null);

  const { dragRef, previewRef, isDragging } = useDashboardCardDrag({
    index,
    rowIndex,
    setSomeDragging,
    column,
    rowId,
  });

  const analysis = analysisMap[analysisId];
  const chart = currDashboard.charts?.find(
    (chart) => chart.alias === analysis?.reference_id,
  );

  const ellipsisMenus = [
    {
      label: 'Refresh data',
      onClick: async () => {
        const res = await service.analysis.syncAnalysis(
          dashboardId,
          analysis.alias,
        );
        setAnalysisItem(analysis.alias, res);
      },
    },
    {
      label: 'Change prompt',
      onClick: async () => {
        const chart = await service.chart.fetchChart(analysis.reference_id);

        const tableQuery = await service.chart.fetchTable(chart);
        const { itemX } = chartUtils.getChartFeedbackListParams(
          chart,
          tableQuery,
        );
        setNewModal<ChartAnalysisFeedbackListModalOptions>({
          alias: chart.alias,
          type: DETAIL_MODAL_TYPE.CHART_ANALYSIS,
          options: {
            chart,
            isAll: true,
            analysis_alias: analysis.alias,
            chart_alias: chart.alias,
            dashboard_alias: dashboardId,
            displayed_x_items: itemX || [],
            dashboardCardInfo: {
              rowIndex: 0,
              dashbaordAlias: dashboardId,
            },
            initialState: CollapseState.EXPANDED,
          },
        });
      },
    },
    {
      label: 'Rename',
      onClick: () => {
        renameModalState.open();
      },
    },
    {
      label: 'Delete',
      onClick: () => {
        track(AMP_KEYS.DASHBOARD_CARD_REMOVE_DASHBOARD_CLICK);
        setPopup({
          popupTheme: 'negative',
          type: 'confirm',
          title: 'Remove from dashboard',
          message:
            'Are you sure you want to remove this smart brief from the dashboard?',
          confirmText: 'Remove',
          cancelText: 'Cancel',
          onConfirm: async () => {
            try {
              await service.dashboard2.deleteColumnInDashboardRow(
                dashboardId,
                rowId,
                column.id,
              );

              // refetch dashboard
              queryClient.refetchQueries({ queryKey: ['dashboard2', dashboardId] });

              addToast('Smart brief removed from dashboard', 'success');
            } catch (e) {
              addToast('Error! try again', 'fail');
            }
          },
        });
      },
    },
  ];

  const onEmptyActionClick = async () => {
    const chart = await service.chart.fetchChart(analysis.reference_id);

    const tableQuery = await service.chart.fetchTable(chart);
    const { itemX } = chartUtils.getChartFeedbackListParams(chart, tableQuery);
    setNewModal<ChartAnalysisFeedbackListModalOptions>({
      alias: chart.alias,
      type: DETAIL_MODAL_TYPE.CHART_ANALYSIS,
      options: {
        chart,
        isAll: true,
        analysis_alias: analysis.alias,
        chart_alias: chart.alias,
        dashboard_alias: dashboardId,
        displayed_x_items: itemX || [],
        dashboardCardInfo: {
          rowIndex: 0,
          dashbaordAlias: dashboardId,
        },
        initialState: CollapseState.EXPANDED,
      },
    });
  };

  const onLinkedChartClick = () => {
    const chartAlias = chart?.alias;
    if (!chartAlias) return;
    const element = document.querySelector(
      `[${DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY}="${chartAlias}"]`,
    );
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add('focus');
    setTimeout(() => {
      element.classList.remove('focus');
    }, 3000);
  };

  if (!analysis) return null;
  return (
    <StyledDashboardCardWrapper
      ref={previewRef}
      isDragging={isDragging}
      width={width}
      style={{ height: '100%' }}
    >
      <StyledDashboardCard
        {...{
          [DASHBOARD_CARD_ANALYSIS_ALIAS_DATASET_KEY]: analysis.alias,
        }}
      >
        <StyledDragArea
          ref={dragRef}
          className="drag-area-handler"
          style={{ top: 26 }}
        >
          <HandlerIcon />
        </StyledDragArea>
        <Transition
          status={true}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Style>
            <DashboardCardHeader>
              <DashboardCardHeaderReferenceSection
                g={4}
                onClick={onLinkedChartClick}
                a="center"
              >
                <LinkOutlined />
                <Typo type="label4" weight="r" ellipsis>
                  Smart brief for
                </Typo>
                <Typo type="label4" weight="sb" ellipsis>
                  {chart?.name}
                </Typo>
              </DashboardCardHeaderReferenceSection>
              <Flex f={1} j="space-between" a="center" padding="0 24px">
                <TooltipV2 tooltipNode="This content is generated by AI. It will be updated periodically.">
                  <Flex g={8} f={1} a="center">
                    <AIIcon />
                    <Typo type="body0" weight="m" multilineEllipsis={2}>
                      {analysis.title}
                    </Typo>
                  </Flex>
                </TooltipV2>
                <Flex a="center" g={8}>
                  <Menu
                    triggerNode={
                      <IconButton
                        icon={<EllipsisOutlined />}
                        outlined={false}
                        sizeType="m"
                        style={{
                          fontSize: '18px',
                        }}
                      />
                    }
                    modalWidth="200px"
                    options={ellipsisMenus}
                  />
                </Flex>
              </Flex>
            </DashboardCardHeader>
            <VFlex f={1} className="content-wrapper">
              <Stage className="flex flex-col flex-1 overflow-auto px-6">
                <Stage.Scene
                  status={analysis.state === AnalysisState.DISCONNECTED}
                >
                  <Empty
                    className="w-62.5 flex-1 self-center"
                    image={
                      <MissingChartIcon
                        width="83px"
                        style={{ marginBottom: -8 }}
                      />
                    }
                    title="Lost connection to the chart"
                    description="The data connection has been lost as the chart has changed."
                    action={{
                      option: 'secondary',
                      text: 'Change prompt',
                      onClick: onEmptyActionClick,
                    }}
                  />
                </Stage.Scene>
                <Stage.Scene
                  status={analysis.state === AnalysisState.IN_PROGRESS}
                >
                  <CenterFlex f={1}>
                    <Spinner />
                  </CenterFlex>
                </Stage.Scene>
                <Stage.Scene
                  status={
                    analysis.state === AnalysisState.OK && !!analysis.chat
                  }
                  ref={chatMessageContainerRef}
                  data={analysis.chat}
                >
                  {(chat) => (
                    <HeySynclyMessageDisplay
                      chat_id={chat.alias}
                      messages={chat.messages}
                      status={chat.status}
                      answer={null}
                      onStudioItemClick={() => {}}
                      container={chatMessageContainerRef.current}
                    />
                  )}
                </Stage.Scene>
              </Stage>
            </VFlex>
            {analysis.state === AnalysisState.OK && (
              <HoverWrapper>
                <Flex padding="0px 24px 18px 24px" g={8} a="center">
                  <Typo type="label3">
                    Updated on{' '}
                    {getTimezoneDayjs(analysis.updated_at).format(
                      DATE_FORMAT.readableHour,
                    )}
                  </Typo>
                  <HoverItem>
                    <IconButton
                      icon={<SettingOutlined />}
                      sizeType="s"
                      outlined={false}
                      onClick={chartAnalysisSettingModalState.open}
                    />
                  </HoverItem>
                </Flex>
              </HoverWrapper>
            )}
          </Style>
        </Transition>
        {dashboard && (
          <Modal
            status={chartAnalysisSettingModalState.status}
            closeModal={chartAnalysisSettingModalState.close}
          >
            <ChartAnalysisSettingModal />
          </Modal>
        )}

        <Modal
          status={renameModalState.status}
          closeModal={renameModalState.close}
        >
          <AnalysisBoxRenameModal
            initialInput={analysis.title}
            onDone={async (name) => {
              const res = await service.analysis.updateAnalysisTitle(
                dashboardId,
                analysis.alias,
                name,
              );
              setAnalysisItem(analysis.alias, res);
            }}
          />
        </Modal>
      </StyledDashboardCard>
    </StyledDashboardCardWrapper>
  );
};

const Style = styled(VFlex)`
  height: 100%;
  .content-wrapper {
    overflow: auto;
    padding: 24px 0px;
    flex: 1;
  }
`;

const DashboardCardHeader = styled(VFlex)`
  width: 100%;
  background-color: ${(props) => props.theme.colors.gray250};
  border-radius: ${radius.token['3xl']}px;
  height: 88px;
`;

const DashboardCardHeaderReferenceSection = styled(Flex)`
  background-color: ${(props) => props.theme.colors.ND04};
  padding: 8px 16px;
  transition: all 0.2s;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.colors.ND08};
    color: ${(props) => props.theme.colors.primary100};
  }
`;

export default DashboardAnalysisBox;
